import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Award, Users, Shield, Mail, Phone, MapPin, Building2, FileBadge2, Calendar, User } from 'lucide-react';
import { SITE } from '../data/mock';

const COMPANY = {
  legalName: 'Adidar Tech India Private Limited',
  cin: 'U62099TN2025PTC181204',
  gstin: '37ABDCA0726C1Z7',
  incorporatedOn: 'August 20, 2015',
  founder: 'Satyanarayana Sodasani',
  registeredOffice: 'Chennai, Tamil Nadu, India',
};

const PILLARS = [
  {
    icon: Scale,
    title: 'Plain-language translation',
    body: 'Statutes, notices and petitions are broken down into simple, regional-language explanations you can actually act on.',
  },
  {
    icon: Users,
    title: 'Verified advocate network',
    body: 'Every lawyer is checked against the State Bar Council enrolment register before being listed on the directory.',
  },
  {
    icon: Shield,
    title: 'No-cost legal aid, always',
    body: 'Citizens who qualify under the Legal Services Authorities Act, 1987 can request free representation and document drafting directly from the platform.',
  },
];

const InfoRow = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3 py-3 border-b border-[#E8DCC0] last:border-b-0" data-testid={testid}>
    <div className="w-9 h-9 rounded-md bg-[#EDE3CC] flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#A36B1F]" strokeWidth={1.7} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] tracking-[0.2em] uppercase text-[#5a5a5a]">{label}</p>
      <p className="text-[15px] text-[#0F1B2D] mt-0.5 font-medium break-words">{value}</p>
    </div>
  </div>
);

const About = () => (
  <div data-testid="about-page">
    <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">ABOUT LAWFORLAYMAN</p>
        <h1 className="font-serif-besley text-4xl md:text-6xl leading-tight mb-6">
          Making Indian law readable, one citizen at a time.
        </h1>
        <p className="text-[#C9BFA8] text-lg max-w-3xl leading-relaxed">
          LawForLayman is a legal-technology platform on a simple mission: put every citizen &mdash;
          irrespective of their income, language or geography &mdash; in the same room as competent
          legal help. We translate statutes into plain speech, connect people to verified advocates
          across India, and coordinate free legal aid for those the system was never designed for.
        </p>
      </div>
    </section>

    <section className="bg-[#F8F2E4] py-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <p className="text-xs tracking-[0.3em] text-[#A36B1F] mb-3">OUR STORY</p>
          <h2 className="font-serif-besley text-3xl md:text-4xl text-[#0F1B2D] mb-6">
            Started in 2015. Built for the next billion.
          </h2>
          <div className="space-y-5 text-[15px] text-[#3a3a3a] leading-relaxed">
            <p>
              LawForLayman began in <b>August 2015</b> as a plain-language legal explainer for
              small-business owners who could not afford a retainer. In the ten years since, it has
              grown into a full legal-tech platform &mdash; still operated by the same team that
              believes India&apos;s laws should never be a paywalled language.
            </p>
            <p>
              Today the platform serves <b>25,000+ citizens</b> across <b>22 states</b>, with a
              verified network of <b>7,000+ advocates</b>. We charge advocates a small platform fee
              to fund the free-aid programme, so that every consultation booked pays forward another
              citizen&apos;s access to justice.
            </p>
            <p>
              LawForLayman is developed and operated by <b>{COMPANY.legalName}</b>, an Indian
              private-limited company. All statutory information is published below for full
              transparency.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <div key={p.title} className="bg-white border border-[#E8DCC0] rounded-2xl p-6">
                <div className="w-11 h-11 rounded-md bg-[#EDE3CC] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-[#A36B1F]" strokeWidth={1.7} />
                </div>
                <h3 className="font-serif-besley text-lg text-[#0F1B2D] mb-2">{p.title}</h3>
                <p className="text-sm text-[#3a3a3a] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-2">
          <div className="bg-white border border-[#E8DCC0] rounded-2xl p-7 sticky top-6" data-testid="company-info-card">
            <p className="text-xs tracking-[0.3em] text-[#A36B1F] mb-3">STATUTORY INFORMATION</p>
            <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-1">{COMPANY.legalName}</h3>
            <p className="text-sm text-[#5a5a5a] mb-4">Operates the LawForLayman platform.</p>

            <InfoRow icon={Building2} label="CIN" value={COMPANY.cin} testid="company-cin" />
            <InfoRow icon={FileBadge2} label="GSTIN" value={COMPANY.gstin} testid="company-gstin" />
            <InfoRow icon={Calendar} label="Incorporated" value={COMPANY.incorporatedOn} testid="company-incorporated" />
            <InfoRow icon={User} label="Founder" value={COMPANY.founder} testid="company-founder" />
            <InfoRow icon={MapPin} label="Registered office" value={COMPANY.registeredOffice} testid="company-office" />
            <InfoRow icon={Mail} label="Support email" value={SITE.email} testid="company-email" />
            <InfoRow icon={Phone} label="Helpline" value={SITE.helpline} testid="company-helpline" />

            <Link
              to="/get-help"
              className="btn-orange inline-flex items-center justify-center gap-2 w-full mt-6 px-5 py-2.5 rounded-md text-sm font-medium"
              data-testid="about-contact-cta"
            >
              <Award className="w-4 h-4" /> Talk to our team
            </Link>
          </div>
        </aside>
      </div>
    </section>
  </div>
);

export default About;
